/**
 * ProductGrid — the wall of label cards, plus the skeleton that stands in for
 * it (STORY-003-005; rebuilt 2026-08 for the counter-slip direction).
 *
 * CSS grid with `repeat(auto-fill, minmax(240px, 1fr))`. At 390px the single
 * track collapses to one column with no horizontal scroll (AC #8). The track
 * came down from 280px because the card is now 4:3 rather than square, so a
 * 240px card is no shorter than a 280px square one used to be — and it buys a
 * fourth column inside the content well, which is what makes a wall of label
 * cards legible AS a grid rather than as three large tiles.
 * `items-stretch` plus `h-full` on the card keeps every card in a row exactly
 * as tall as its neighbours, so the price rules run straight across the page.
 *
 * SKELETON. The old one was three grey bars of arbitrary width under a square:
 * a placeholder for "some card", not for THIS card. It now mirrors the label
 * grid rule-for-rule — photo, name block, measure block, price block, with the
 * real drawn rules already in place — so the layout does not jump when data
 * lands, and so a slow connection in a bazaar shows the shape of the answer
 * rather than abstract noise. It uses `.g-skel` (the theme's shimmer, which
 * tracks light/dark and stops under prefers-reduced-motion) instead of
 * `animate-pulse`, which pulsed a hardcoded secondary fill.
 *
 * Motion (house style): on FIRST paint of a given `entranceKey` the `<ul>`
 * staggers its first ~8 cards in (fade+rise) using the shared
 * `containerVariants` / `itemVariants`. Subsequent renders for the same key —
 * "Load more" appends, a count refresh — do NOT re-stagger; only a NEW key (a
 * fresh search query) re-runs the entrance. It uses a raw `motion.ul` /
 * `motion.li` rather than the `<Stagger>` wrapper so the grid's `aria-label`
 * and the inline auto-fill `grid-template-columns` stay on the SAME node as the
 * `product-grid` testid. Everything is gated on `prefers-reduced-motion`.
 */
import { useRef, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import { ProductCard } from "@/features/marketplace/ProductCard";
import { containerVariants, itemVariants } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";
import type { ProductSearchItem } from "@/features/marketplace/useMarketplaceBrowse";

/** Number of skeleton placeholder cards on first load (AC #10). */
export const SKELETON_CARD_COUNT = 8;

/** Inline grid-template so the auto-fill track is exact. */
const GRID_STYLE: React.CSSProperties = {
  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
};

export interface ProductGridProps {
  items: readonly ProductSearchItem[];
  isLoading: boolean;
  onCardClick?: (product: ProductSearchItem, position: number) => void;
  /**
   * Identity of the current result set (e.g. the trimmed search query, or a
   * stable token for browse). The entrance stagger fires ONCE per distinct key:
   * a new key re-animates the grid; appends / refetches under the same key do
   * not. Defaults to a stable token so browse animates only on first paint.
   */
  entranceKey?: string;
}

/** A skeleton built on the SAME three rules as the card it stands in for. */
function SkeletonCard({ index }: { index: number }): ReactElement {
  return (
    <div
      aria-hidden="true"
      data-testid={`product-skeleton-${index}`}
      className="flex h-full flex-col overflow-hidden rounded-[var(--g-radius)] border border-[color:var(--g-hairline)] bg-card"
    >
      <div className="g-skel aspect-[4/3] w-full" />
      <div className="g-ruled flex flex-1 flex-col border-t border-[color:var(--g-rule-strong)]">
        {/* Name row — two clamped lines. */}
        <div className="flex min-h-[3.25rem] flex-col gap-1.5 px-3 py-3">
          <div className="g-skel h-3 w-11/12 rounded-full" />
          <div className="g-skel h-3 w-2/3 rounded-full" />
        </div>
        {/* Measure row. */}
        <div className="flex flex-col gap-1.5 px-3 py-2.5">
          <div className="g-skel h-3 w-1/2 rounded-full" />
          <div className="g-skel h-2.5 w-2/5 rounded-full" />
        </div>
        {/* Price row — wider and taller, because the figure is the hero. */}
        <div className="px-3 py-3">
          <div className="g-skel h-5 w-3/5 rounded-[4px]" />
        </div>
      </div>
    </div>
  );
}

export function ProductGrid({
  items,
  isLoading,
  onCardClick,
  entranceKey = "default",
}: ProductGridProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const { reduced } = useGardenMotion();

  // First-paint gate: remember the last key whose GRID we already animated. A
  // NEW key (a fresh search query) re-runs the entrance; the same key
  // re-rendering with more items ("Load more" appends) or a refetch does NOT
  // re-stagger. Under reduced motion nothing animates, so the gate is a no-op.
  const animatedKeyRef = useRef<string | null>(null);

  // The skeleton paint must NOT consume the first-paint flag — otherwise the
  // real grid (which arrives on the next paint of the same key) would mount
  // already-settled. So the ref is only updated once we render the grid below.
  if (isLoading) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label={t("browse.loadingMore")}
        data-testid="product-grid-skeleton"
        className="grid items-stretch gap-4"
        style={GRID_STYLE}
      >
        {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
          <SkeletonCard key={i} index={i} />
        ))}
      </div>
    );
  }

  const animate = !reduced && animatedKeyRef.current !== entranceKey;
  // Consume the first-paint flag now that the grid itself is rendering.
  if (animatedKeyRef.current !== entranceKey) animatedKeyRef.current = entranceKey;

  return (
    <motion.ul
      aria-label={t("aria.productGrid")}
      data-testid="product-grid"
      className="m-0 grid list-none items-stretch gap-4 p-0"
      style={GRID_STYLE}
      variants={animate ? containerVariants : undefined}
      initial={animate ? "hidden" : false}
      animate={animate ? "show" : undefined}
    >
      {items.map((product, index) => {
        const card = (
          <ProductCard
            product={product}
            position={index}
            onCardClick={onCardClick}
          />
        );
        // Cap the per-item stagger to the first ~8 cards on a first paint of
        // the key; later cards mount in their final state so a 50-item page
        // does not ripple. `h-full` keeps a staggered <li> the height of its
        // row so the card can stretch to it.
        const staggered = animate && index < SKELETON_CARD_COUNT;
        return staggered ? (
          <motion.li key={product.id} variants={itemVariants} className="h-full">
            {card}
          </motion.li>
        ) : (
          <li key={product.id} className="h-full">
            {card}
          </li>
        );
      })}
    </motion.ul>
  );
}
